import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { errMsg } from "@/lib/api";
import { t } from "@/i18n";

export const ymdIn = (tz, d = new Date()) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
export const addDays = (ymd, n) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
export const fmtTime = (iso, tz) => new Intl.DateTimeFormat("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz }).format(new Date(iso));
export const fmtDayLong = (ymdOrIso, tz) => new Intl.DateTimeFormat("en-AU", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric", timeZone: tz })
  .format(new Date(ymdOrIso.length === 10 ? `${ymdOrIso}T12:00:00Z` : ymdOrIso));
export const minutesIn = (iso, tz) => {
  const [h, m] = fmtTime(iso, tz).split(":").map(Number);
  return h * 60 + m;
};

export function SlotPicker({ load, tz, value, onChange, showMechanics, testPrefix = "slot" }) {
  const today = ymdIn(tz);
  const [day, setDay] = useState(today);
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let live = true;
    setState({ loading: true });
    load(day).then((slots) => live && setState({ slots })).catch((e) => live && setState({ error: errMsg(e) }));
    return () => { live = false; };
  }, [day, load]);
  return (
    <div className="space-y-4" data-testid={`${testPrefix}-picker`}>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="icon" disabled={day <= today} onClick={() => setDay(addDays(day, -1))} data-testid={`${testPrefix}-prev-day`} aria-label={t("sched.prevDay")}><ChevronLeft className="h-4 w-4" /></Button>
        <Input type="date" min={today} value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-44" data-testid={`${testPrefix}-date-input`} />
        <Button type="button" variant="outline" size="icon" onClick={() => setDay(addDays(day, 1))} data-testid={`${testPrefix}-next-day`} aria-label={t("sched.nextDay")}><ChevronRight className="h-4 w-4" /></Button>
        <span className="text-sm text-slate-600">{fmtDayLong(day, tz)}</span>
      </div>
      {state.loading && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("sched.loadingSlots")}</p>}
      {state.error && <p className="text-sm text-red-600" data-testid={`${testPrefix}-error`}>{state.error}</p>}
      {state.slots?.length === 0 && <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-600" data-testid={`${testPrefix}-empty`}>{t("sched.noSlots")}</p>}
      {state.slots?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-6">
          {state.slots.map((s) => {
            const mechs = showMechanics ? s.mechanics : [null];
            return mechs.map((m) => {
              const key = `${s.start}-${m?.user_id || ""}`;
              const active = value?.start === s.start && (!showMechanics || value?.mechanic_id === m.user_id);
              return (
                <button key={key} type="button" onClick={() => onChange({ start: s.start, time: s.time, mechanic_id: m?.user_id, mechanic_name: m?.name })}
                  data-testid={`${testPrefix}-option-${s.time.replace(":", "")}${m ? `-${m.name.split(" ")[0].toLowerCase()}` : ""}`}
                  className={`rounded-lg border px-2 py-2 text-sm font-semibold transition-colors duration-150 ${active ? "border-[var(--brand,#0A2540)] bg-[var(--brand,#0A2540)] text-[var(--brand-contrast,#fff)]" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                  {s.time}{m && <span className="block text-[11px] font-normal opacity-80">{m.name}</span>}
                </button>
              );
            });
          })}
        </div>
      )}
    </div>
  );
}

export function NotificationBell({ load, markRead, testId = "notification-bell", light, linkFor }) {
  const [items, setItems] = useState([]);
  const refresh = () => load().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const unread = items.filter((n) => !n.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) markRead().then(refresh).catch(() => {}); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className={`relative ${light ? "text-slate-200 hover:bg-white/10 hover:text-white" : ""}`} data-testid={testId} aria-label={t("notif.title")}>
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white" data-testid={`${testId}-count`}>{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid={`${testId}-panel`}>
        <p className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">{t("notif.title")}</p>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <p className="p-4 text-sm text-slate-500">{t("notif.empty")}</p>}
          {items.map((n) => {
            const to = linkFor?.(n);
            const body = (<>
              <p className="font-medium">{t(`notif.kinds.${n.kind}`)}</p>
              {n.text && <p className="text-xs">{n.text}</p>}
              <p className="text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString("en-AU")}</p>
            </>);
            const cls = `block border-b border-slate-50 px-4 py-3 text-sm ${n.read ? "text-slate-500" : "bg-teal-50/50 text-slate-900"} ${to ? "hover:bg-slate-50" : ""}`;
            return to ? <Link key={n.id} to={to} className={cls} data-testid={`notif-link-${n.id}`}>{body}</Link> : <div key={n.id} className={cls}>{body}</div>;
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
